import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, FileText, Palette, Download, Sparkles, Target } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ResumeBuilderQuestions from '../components/ResumeBuilderQuestions';
import TemplateRecommendation from '../components/TemplateRecommendation';
import TemplateLibrary from '../components/TemplateLibrary';
import ResumeTemplatePreview from '../components/ResumeTemplatePreview';
import TemplateCustomizer from '../components/TemplateCustomizer';

const ResumeBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState('questions'); // questions, recommendations, templates, editor
  const [userAnswers, setUserAnswers] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCustomizerOpen, setIsCustomizerOpen] = useState(false);
  const [templateCustomization, setTemplateCustomization] = useState({});
  const [isDownloading, setIsDownloading] = useState(false);
  const [showProfilePicture, setShowProfilePicture] = useState(false);
  const resumeRef = useRef(null);

  // Resume data state for live editing
  const [resumeData, setResumeData] = useState({
    personalInfo: {
      fullName: 'John Doe',
      jobTitle: 'Software Engineer',
      email: 'john.doe@email.com',
      phone: '(555) 123-4567',
      location: '',
      website: '',
      linkedin: '',
      github: '',
      profilePicture: ''
    },
    summary: 'Experienced software engineer with 5+ years of experience in full-stack development...',
    experience: [
      {
        id: 1,
        title: 'Senior Software Engineer',
        company: 'Tech Company',
        location: 'San Francisco, CA',
        startDate: '2020',
        endDate: 'Present',
        description: 'Led development of web applications using React and Node.js...',
        current: true
      }
    ],
    education: [
      {
        id: 1,
        degree: 'Bachelor of Science in Computer Science',
        institution: 'University Name',
        location: 'San Francisco, CA',
        graduationYear: '2018',
        gpa: '3.8'
      }
    ],
    skills: ['JavaScript', 'React', 'Node.js', 'Python', 'SQL'],
    certifications: [],
    projects: []
  });

  // Handle incoming state from AI recommendations or other pages
  useEffect(() => {
    if (location.state) {
      if (location.state.selectedTemplate) {
        setSelectedTemplate(location.state.selectedTemplate);
      }
      if (location.state.currentStep) {
        setCurrentStep(location.state.currentStep);
      }
      // If AI-generated resume content is provided, populate the resume data
      if (location.state.resumeContent) {
        setResumeData(location.state.resumeContent);
        // Set toggle based on whether profile picture exists
        setShowProfilePicture(!!location.state.resumeContent.personalInfo?.profilePicture);
      }
      // Clear the location state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);
  
  // Update toggle state when profile picture changes
  useEffect(() => {
    if (resumeData.personalInfo?.profilePicture) {
      setShowProfilePicture(true);
    }
  }, [resumeData.personalInfo?.profilePicture]);

  console.log('ResumeBuilder rendered, currentStep:', currentStep);

  const handleQuestionsComplete = (answers) => {
    setUserAnswers(answers);
    setCurrentStep('recommendations');
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCurrentStep('editor');
  };

  const handleBackToTemplates = () => {
    setCurrentStep('templates');
  };

  const handleBackToRecommendations = () => {
    setCurrentStep('recommendations');
  };

  const handleBackToQuestions = () => {
    setCurrentStep('questions');
  };

  // Live editing functions
  const updatePersonalInfo = (field, value) => {
    setResumeData(prev => ({
      ...prev,
      personalInfo: {
        ...prev.personalInfo,
        [field]: value
      }
    }));
  };

  const updateSummary = (value) => {
    setResumeData(prev => ({
      ...prev,
      summary: value
    }));
  };

  const addExperience = () => {
    const newExperience = {
      id: Date.now(),
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      description: '',
      current: false
    };
    setResumeData(prev => ({
      ...prev,
      experience: [...prev.experience, newExperience]
    }));
  };

  const updateExperience = (id, field, value) => {
    setResumeData(prev => ({
      ...prev,
      experience: prev.experience.map(exp => 
        exp.id === id ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const addEducation = () => {
    const newEducation = {
      id: Date.now(),
      degree: '',
      institution: '',
      location: '',
      graduationYear: '',
      gpa: ''
    };
    setResumeData(prev => ({
      ...prev,
      education: [...prev.education, newEducation]
    }));
  };

  const updateEducation = (id, field, value) => {
    setResumeData(prev => ({
      ...prev,
      education: prev.education.map(edu =>
        edu.id === id ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (id) => {
    setResumeData(prev => ({
      ...prev,
      education: prev.education.filter(edu => edu.id !== id)
    }));
  };

  const addSkill = () => {
    const newSkill = prompt('Enter a skill:');
    if (newSkill && newSkill.trim()) {
      setResumeData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
    }
  };

  const addMultipleSkills = () => {
    const skillsInput = prompt('Enter skills separated by commas (e.g., JavaScript, React, Node.js):');
    if (skillsInput && skillsInput.trim()) {
      const skills = skillsInput.split(',').map(skill => skill.trim()).filter(skill => skill);
      setResumeData(prev => ({
        ...prev,
        skills: [...prev.skills, ...skills]
      }));
    }
  };

  const removeSkill = (index) => {
    setResumeData(prev => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index)
    }));
  };

  const addCertificate = () => {
    const newCertificate = {
      id: Date.now(),
      name: '',
      issuer: '',
      date: '',
      credentialId: ''
    };
    setResumeData(prev => ({
      ...prev,
      certifications: [...prev.certifications, newCertificate]
    }));
  };

  const updateCertificate = (id, field, value) => {
    setResumeData(prev => ({
      ...prev,
      certifications: prev.certifications.map(cert =>
        cert.id === id ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const removeCertificate = (id) => {
    setResumeData(prev => ({
      ...prev,
      certifications: prev.certifications.filter(cert => cert.id !== id)
    }));
  };

  const removeExperience = (id) => {
    setResumeData(prev => ({
      ...prev,
      experience: prev.experience.filter(exp => exp.id !== id)
    }));
  };

  const handleCustomize = (customization) => {
    setTemplateCustomization(customization);
    setIsCustomizerOpen(false);
  };

  const downloadResume = async () => {
    if (!resumeRef.current) {
      console.error('Resume ref not found');
      alert('Resume preview not found. Please try refreshing the page.');
      return;
    }

    setIsDownloading(true);
    
    try {
      // Get the resume element
      const resumeElement = resumeRef.current;
      
      // Wait a moment for any animations to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Configure html2canvas options for better quality
      const canvas = await html2canvas(resumeElement, {
        scale: 3, // Higher scale for better quality
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: resumeElement.scrollWidth,
        height: resumeElement.scrollHeight,
        scrollX: 0,
        scrollY: 0,
        logging: false, // Disable logging for cleaner console
        onclone: (clonedDoc) => {
          // Ensure all styles are applied in the cloned document
          const clonedElement = clonedDoc.querySelector('[data-resume-preview]');
          if (clonedElement) {
            clonedElement.style.transform = 'none';
            clonedElement.style.position = 'static';
          }
        }
      });

      // Calculate PDF dimensions (A4 size: 210mm x 297mm)
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // If the content is taller than one page, we need to split it
      if (imgHeight > pageHeight) {
        const totalPages = Math.ceil(imgHeight / pageHeight);
        
        for (let i = 0; i < totalPages; i++) {
          if (i > 0) {
            pdf.addPage();
          }
          
          const yOffset = -i * pageHeight;
          pdf.addImage(
            canvas.toDataURL('image/png', 1.0), // Maximum quality
            'PNG',
            0,
            yOffset,
            imgWidth,
            imgHeight
          );
        }
      } else {
        // Single page
        pdf.addImage(
          canvas.toDataURL('image/png', 1.0), // Maximum quality
          'PNG',
          0,
          0,
          imgWidth,
          imgHeight
        );
      }

      // Generate filename with timestamp to avoid conflicts
      const timestamp = new Date().toISOString().slice(0, 10);
      const cleanName = resumeData.personalInfo.fullName
        .replace(/[^a-zA-Z0-9\s]/g, '') // Remove special characters
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .substring(0, 50); // Limit length
      const fileName = `${cleanName}_Resume_${timestamp}.pdf`;
      
      // Download the PDF
      pdf.save(fileName);
      
      // Show success message (optional - the download itself is the main feedback)
      console.log('Resume downloaded successfully!');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again. If the problem persists, try refreshing the page.');
    } finally {
      setIsDownloading(false);
    }
  };

  const renderStep = () => {
    console.log('renderStep called with currentStep:', currentStep);
    switch (currentStep) {
      case 'questions':
        return (
          <ResumeBuilderQuestions onComplete={handleQuestionsComplete} />
        );
      case 'recommendations':
        return (
          <TemplateRecommendation 
            answers={userAnswers} 
            onSelectTemplate={handleTemplateSelect}
          />
        );
      case 'templates':
        return (
          <TemplateLibrary onSelectTemplate={handleTemplateSelect} />
        );
      case 'editor':
        return (
          <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4">
              {/* Header */}
              <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={handleBackToTemplates}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h1 className="text-2xl font-bold text-gray-900">
                        Resume Editor
                      </h1>
                      <p className="text-gray-600">
                        Template: {selectedTemplate?.name || 'Custom Template'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button 
                      onClick={() => navigate('/ai-recommendations')}
                      className="flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-300 shadow-md hover:shadow-lg"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      AI Recommendations
                    </button>
                    <button 
                      onClick={() => setIsCustomizerOpen(true)}
                      className="flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Palette className="w-4 h-4 mr-2" />
                      Customize
                    </button>
                    <button 
                      onClick={() => navigate('/resume-analyzer', { state: { resumeData } })}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <Target className="w-4 h-4 mr-2" />
                      Analyze Resume
                    </button>
                    <button 
                      onClick={downloadResume}
                      disabled={isDownloading}
                      className={`flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors ${
                        isDownloading ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {isDownloading ? 'Generating PDF...' : 'Download PDF'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Resume Editor Content */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Resume Preview */}
                        <div className="lg:col-span-2">
                          <div className="bg-white rounded-lg shadow-lg p-8">
                            <div className="aspect-[8.5/11] bg-gray-50 border-2 border-gray-200 rounded-lg p-8">
                              {/* Live Resume Content using selected template */}
                              <div ref={resumeRef} data-resume-preview className="h-full">
                                <ResumeTemplatePreview 
                                  template={selectedTemplate} 
                                  resumeData={resumeData}
                                  customization={templateCustomization}
                                  className="h-full"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                {/* Editor Sidebar */}
                <div className="space-y-6">
                  {/* Personal Information */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Personal Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          value={resumeData.personalInfo.fullName}
                          onChange={(e) => updatePersonalInfo('fullName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="Enter your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Job Title
                        </label>
                        <input
                          type="text"
                          value={resumeData.personalInfo.jobTitle}
                          onChange={(e) => updatePersonalInfo('jobTitle', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="e.g., Software Engineer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          value={resumeData.personalInfo.email}
                          onChange={(e) => updatePersonalInfo('email', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="your.email@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          value={resumeData.personalInfo.phone}
                          onChange={(e) => updatePersonalInfo('phone', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          value={resumeData.personalInfo.location}
                          onChange={(e) => updatePersonalInfo('location', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          placeholder="City, State"
                        />
                      </div>
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="block text-sm font-medium text-gray-700">
                            Profile Picture
                          </label>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm ${showProfilePicture ? 'text-gray-700' : 'text-gray-400'}`}>
                              {showProfilePicture ? 'Enabled' : 'Disabled'}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                const newValue = !showProfilePicture;
                                setShowProfilePicture(newValue);
                                if (!newValue) {
                                  // Clear profile picture when toggle is off
                                  updatePersonalInfo('profilePicture', '');
                                }
                              }}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                showProfilePicture ? 'bg-primary-600' : 'bg-gray-300'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  showProfilePicture ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                        
                        {showProfilePicture && (
                          <>
                            <div className="flex items-center gap-4">
                              {resumeData.personalInfo.profilePicture && (
                                <div className="relative">
                                  <img 
                                    src={resumeData.personalInfo.profilePicture} 
                                    alt="Profile" 
                                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-300 shadow-md"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => updatePersonalInfo('profilePicture', '')}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                    title="Remove picture"
                                  >
                                    ×
                                  </button>
                                </div>
                              )}
                              <div className="flex-1">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => {
                                    const file = e.target.files[0];
                                    if (file) {
                                      // Validate file size (max 5MB)
                                      if (file.size > 5 * 1024 * 1024) {
                                        alert('Image size should be less than 5MB');
                                        return;
                                      }
                                      // Validate file type
                                      if (!file.type.startsWith('image/')) {
                                        alert('Please select a valid image file');
                                        return;
                                      }
                                      const reader = new FileReader();
                                      reader.onloadend = () => {
                                        updatePersonalInfo('profilePicture', reader.result);
                                      };
                                      reader.onerror = () => {
                                        alert('Error reading image file');
                                      };
                                      reader.readAsDataURL(file);
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                />
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Upload a professional photo to include in your resume (appears on the top left side). Max size: 5MB
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Professional Summary */}
                  <div className="bg-white rounded-lg shadow-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Professional Summary
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Summary
                      </label>
                      <textarea
                        value={resumeData.summary}
                        onChange={(e) => updateSummary(e.target.value)}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        placeholder="Write a brief summary of your professional background..."
                      />
                    </div>
                  </div>

                          {/* Quick Actions */}
                          <div className="bg-white rounded-lg shadow-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                              Quick Actions
                            </h3>
                            <div className="space-y-3">
                              <button
                                onClick={addExperience}
                                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                              >
                                <FileText className="w-4 h-4 inline mr-2" />
                                Add Work Experience
                              </button>
                              <button
                                onClick={addEducation}
                                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                              >
                                <FileText className="w-4 h-4 inline mr-2" />
                                Add Education
                              </button>
                              <button
                                onClick={addMultipleSkills}
                                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                              >
                                <FileText className="w-4 h-4 inline mr-2" />
                                Add Skills
                              </button>
                              <button
                                onClick={addCertificate}
                                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                              >
                                <FileText className="w-4 h-4 inline mr-2" />
                                Add Certifications
                              </button>
                            </div>
                          </div>

                          {/* Work Experience Editor */}
                          {resumeData.experience.length > 0 && (
                            <div className="bg-white rounded-lg shadow-lg p-6">
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Work Experience
                              </h3>
                              <div className="space-y-4">
                                {resumeData.experience.map((exp) => (
                                  <div key={exp.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Job Title
                                        </label>
                                        <input
                                          type="text"
                                          value={exp.title}
                                          onChange={(e) => updateExperience(exp.id, 'title', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., Senior Software Engineer"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Company
                                        </label>
                                        <input
                                          type="text"
                                          value={exp.company}
                                          onChange={(e) => updateExperience(exp.id, 'company', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., Tech Company Inc."
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Location
                                        </label>
                                        <input
                                          type="text"
                                          value={exp.location}
                                          onChange={(e) => updateExperience(exp.id, 'location', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., San Francisco, CA"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Start Date
                                        </label>
                                        <input
                                          type="text"
                                          value={exp.startDate}
                                          onChange={(e) => updateExperience(exp.id, 'startDate', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., 2020"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          End Date
                                        </label>
                                        <input
                                          type="text"
                                          value={exp.endDate}
                                          onChange={(e) => updateExperience(exp.id, 'endDate', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., Present or 2023"
                                        />
                                      </div>
                                      <div className="flex items-center">
                                        <label className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={exp.current}
                                            onChange={(e) => updateExperience(exp.id, 'current', e.target.checked)}
                                            className="mr-2"
                                          />
                                          <span className="text-sm text-gray-700">Currently working here</span>
                                        </label>
                                      </div>
                                    </div>
                                    <div className="mb-4">
                                      <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description
                                      </label>
                                      <textarea
                                        value={exp.description}
                                        onChange={(e) => updateExperience(exp.id, 'description', e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        placeholder="Describe your responsibilities and achievements..."
                                      />
                                    </div>
                                    <button
                                      onClick={() => removeExperience(exp.id)}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove Experience
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Education Editor */}
                          {resumeData.education.length > 0 && (
                            <div className="bg-white rounded-lg shadow-lg p-6">
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Education
                              </h3>
                              <div className="space-y-4">
                                {resumeData.education.map((edu) => (
                                  <div key={edu.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Degree
                                        </label>
                                        <input
                                          type="text"
                                          value={edu.degree}
                                          onChange={(e) => updateEducation(edu.id, 'degree', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., Bachelor of Science in Computer Science"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Institution
                                        </label>
                                        <input
                                          type="text"
                                          value={edu.institution}
                                          onChange={(e) => updateEducation(edu.id, 'institution', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., University of California"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Location
                                        </label>
                                        <input
                                          type="text"
                                          value={edu.location}
                                          onChange={(e) => updateEducation(edu.id, 'location', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., Berkeley, CA"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Graduation Year
                                        </label>
                                        <input
                                          type="text"
                                          value={edu.graduationYear}
                                          onChange={(e) => updateEducation(edu.id, 'graduationYear', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., 2020"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          GPA (Optional)
                                        </label>
                                        <input
                                          type="text"
                                          value={edu.gpa}
                                          onChange={(e) => updateEducation(edu.id, 'gpa', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., 3.8"
                                        />
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => removeEducation(edu.id)}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove Education
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Skills Editor */}
                          <div className="bg-white rounded-lg shadow-lg p-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">
                              Skills
                            </h3>
                            {resumeData.skills.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-4">
                                {resumeData.skills.map((skill, index) => (
                                  <div key={index} className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                                    <span>{skill}</span>
                                    <button
                                      onClick={() => removeSkill(index)}
                                      className="ml-2 text-blue-600 hover:text-blue-800"
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex space-x-2">
                              <button
                                onClick={addSkill}
                                className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                              >
                                Add Single Skill
                              </button>
                              <button
                                onClick={addMultipleSkills}
                                className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                              >
                                Add Multiple Skills
                              </button>
                            </div>
                          </div>

                          {/* Certifications Editor */}
                          {resumeData.certifications.length > 0 && (
                            <div className="bg-white rounded-lg shadow-lg p-6">
                              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                Certifications
                              </h3>
                              <div className="space-y-4">
                                {resumeData.certifications.map((cert) => (
                                  <div key={cert.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Certificate Name
                                        </label>
                                        <input
                                          type="text"
                                          value={cert.name}
                                          onChange={(e) => updateCertificate(cert.id, 'name', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., AWS Certified Solutions Architect"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Issuing Organization
                                        </label>
                                        <input
                                          type="text"
                                          value={cert.issuer}
                                          onChange={(e) => updateCertificate(cert.id, 'issuer', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., Amazon Web Services"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Date
                                        </label>
                                        <input
                                          type="text"
                                          value={cert.date}
                                          onChange={(e) => updateCertificate(cert.id, 'date', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., December 2023"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Credential ID (Optional)
                                        </label>
                                        <input
                                          type="text"
                                          value={cert.credentialId}
                                          onChange={(e) => updateCertificate(cert.id, 'credentialId', e.target.value)}
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                          placeholder="e.g., AWS-123456"
                                        />
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => removeCertificate(cert.id)}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove Certification
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="max-w-4xl mx-auto px-4">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Resume Builder
              </h1>
              <p className="text-gray-600 mb-4">
                Something went wrong. Please try again.
              </p>
              <button
                onClick={() => setCurrentStep('questions')}
                className="btn-primary"
              >
                Start Building Resume
              </button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <AnimatePresence mode="wait">
          {renderStep()}
        </AnimatePresence>
      </div>
      <Footer />

      {/* Template Customizer Modal */}
      <TemplateCustomizer
        isOpen={isCustomizerOpen}
        onClose={() => setIsCustomizerOpen(false)}
        template={selectedTemplate}
        onCustomize={handleCustomize}
        currentCustomization={templateCustomization}
      />
    </div>
  );
};

export default ResumeBuilder;
